import { useState } from 'react';
import { Info, Lightbulb, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { HelpContent, HelpStepItem } from '@/data/help-content/types';
import type { RolePermissions } from '@/types/roles.types';

interface Props {
  content: HelpContent;
  permissions: RolePermissions;
  searchQuery: string;
}

function resolveDotPath(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[key];
    return undefined;
  }, obj);
}

function isVisible(permissions: RolePermissions, requiredPermission?: string): boolean {
  if (!requiredPermission) return true;
  return resolveDotPath(permissions as unknown as Record<string, unknown>, requiredPermission) === true;
}

function matchesSearch(text: string, query: string): boolean {
  if (!query) return true;
  return text.toLowerCase().includes(query.toLowerCase());
}

function stepMatchesSearch(step: HelpContent['userGuide']['steps'][0], query: string): boolean {
  if (!query) return true;
  if (matchesSearch(step.title, query)) return true;
  return step.items.some(
    (item) =>
      matchesSearch(item.text, query) ||
      (item.note ? matchesSearch(item.note, query) : false) ||
      (item.tip ? matchesSearch(item.tip, query) : false) ||
      (item.subSteps ? item.subSteps.some((s) => matchesSearch(s, query)) : false),
  );
}

interface StepItemProps {
  item: HelpStepItem;
  index: number;
}

function StepItemRow({ item, index }: StepItemProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground mt-0.5">
          {index + 1}
        </span>
        <p className="text-sm leading-relaxed">{item.text}</p>
      </div>

      {item.note && (
        <div className="ml-8 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/40 px-3 py-2">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-blue-600 dark:text-blue-400" />
          <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">{item.note}</p>
        </div>
      )}

      {item.tip && (
        <div className="ml-8 flex items-start gap-2 rounded-md border border-warning-200 bg-warning-50 dark:border-warning-800 dark:bg-warning-950/40 px-3 py-2">
          <Lightbulb className="h-3.5 w-3.5 shrink-0 mt-0.5 text-warning-600 dark:text-warning-400" />
          <p className="text-xs text-warning-700 dark:text-warning-300 leading-relaxed">{item.tip}</p>
        </div>
      )}

      {item.subSteps && item.subSteps.length > 0 && (
        <ul className="ml-8 space-y-1">
          {item.subSteps.map((sub, si) => (
            <li key={si} className="flex items-start gap-2 text-sm text-muted-foreground">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/50" />
              {sub}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface AccordionItemProps {
  title: string;
  items: HelpStepItem[];
  isOpen: boolean;
  onToggle: () => void;
}

function AccordionItem({ title, items, isOpen, onToggle }: AccordionItemProps) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-accent/40 transition-colors"
        aria-expanded={isOpen}
      >
        <span className="text-sm font-medium">{title}</span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200', isOpen && 'rotate-180')}
        />
      </button>
      {isOpen && (
        <div className="border-t border-border px-4 py-4 space-y-4">
          {items.map((item, i) => (
            <StepItemRow key={i} item={item} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}

export function UserGuideTab({ content, permissions, searchQuery }: Props) {
  const { userGuide } = content;
  const [openIndexes, setOpenIndexes] = useState<Set<number>>(new Set([0]));

  const visibleSteps = userGuide.steps.filter(
    (step) =>
      isVisible(permissions, step.requiredPermission) &&
      stepMatchesSearch(step, searchQuery),
  );

  const gettingStartedMatches = matchesSearch(userGuide.gettingStarted, searchQuery);
  const visibleProTips = userGuide.proTips.filter((t) => matchesSearch(t, searchQuery));

  const hasResults = gettingStartedMatches || visibleSteps.length > 0 || visibleProTips.length > 0;

  const toggle = (i: number) => {
    setOpenIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  if (!hasResults && searchQuery) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-sm text-muted-foreground">No results found for "{searchQuery}"</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Getting Started callout */}
      {gettingStartedMatches && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">Getting Started</p>
          <p className="text-sm leading-relaxed">{userGuide.gettingStarted}</p>
        </div>
      )}

      {/* Step-by-step accordion */}
      {visibleSteps.length > 0 && (
        <div className="space-y-2">
          {visibleSteps.map((step, i) => (
            <AccordionItem
              key={step.title}
              title={step.title}
              items={step.items}
              isOpen={openIndexes.has(i)}
              onToggle={() => toggle(i)}
            />
          ))}
        </div>
      )}

      {/* Pro Tips */}
      {visibleProTips.length > 0 && (
        <div className="rounded-lg border border-warning-200 bg-warning-50 dark:border-warning-800 dark:bg-warning-950/30 px-4 py-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-warning-700 dark:text-warning-400 mb-3">
            Pro Tips
          </p>
          <ul className="space-y-2">
            {visibleProTips.map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-warning-800 dark:text-warning-300">
                <Lightbulb className="h-3.5 w-3.5 shrink-0 mt-0.5 text-warning-600 dark:text-warning-400" />
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
